<template>
  <v-container fluid>
    <v-card>
      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(submit)">
          <i-toolbar
            :title="
              expense.pk
                ? `${$t('edit')} ${$tc(title, 1)}`
                : `${$t('add', { value: $tc(title, 1) })}`
            "
            :loading="load"
          ></i-toolbar>
          <v-card-text>
            <v-row class="py-5">
              <!-- Date Picker -->
              <v-col cols="12" lg="3" md="3" xs="12" pr-4>
                <i-date-picker
                  v-model="expense.start_date"
                  :disabled="isPaymentFlow"
                  :label="recurrent ? $tc('start_date', 2) : $t('date')"
                  color="primary"
                  :max="!recurrent ? today : ''"
                  :min="recurrent ? today : ''"
                />
              </v-col>

              <!-- Account Selection -->
              <v-col cols="12" lg="3" md="3" sm="6">
                <dialAccount
                  v-model="dialogAccount"
                  :to-edit="toEditAccount"
                  @done="addAccount"
                />
                <ValidationProvider
                  vid="account"
                  :name="$tc('account', 1)"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-autocomplete
                    autocomplete="off"
                    outlined
                    :disabled="isPaymentFlow"
                    :clearable="accounts.length > 10"
                    v-model="expense.account_id"
                    :items="accounts"
                    item-text="name"
                    item-value="pk"
                    :label="$tc('account', 2)"
                    :error-messages="errors[0]"
                    :append-icon="accounts.length > 10 ? 'fa-search' : ''"
                    :prepend-inner-icon="
                      getPermissions([`expenses.add_account`]) || isAdmin
                        ? 'fa-plus'
                        : ''
                    "
                    @click:prepend-inner="openAccountModal"
                  />
                </ValidationProvider>
              </v-col>

              <!-- Category Selection -->
              <v-col cols="12" lg="3" md="3" sm="6">
                <dialCategory
                  v-model="dialogCategory"
                  :to-edit="toEditCategory"
                  @done="addCategory"
                />
                <ValidationProvider
                  vid="category"
                  :name="$tc('category', 1)"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-autocomplete
                    autocomplete="off"
                    outlined
                    :disabled="isPaymentFlow"
                    :clearable="categories.length > 10"
                    v-model="expense.category_id"
                    :items="categories"
                    item-text="name"
                    item-value="pk"
                    :label="$tc('category', 2)"
                    :error-messages="errors[0]"
                    :append-icon="categories.length > 10 ? 'fa-search' : ''"
                    :prepend-inner-icon="
                      getPermissions([`expenses.add_category`]) || isAdmin
                        ? 'fa-plus'
                        : ''
                    "
                    @click:prepend-inner="openCategoryModal"
                  />
                </ValidationProvider>
              </v-col>

              <!-- Operator Field -->
              <v-col
                v-if="!recurrent || isPaymentFlow"
                cols="12"
                lg="3"
                md="3"
                sm="6"
              >
                <ValidationProvider
                  vid="operator"
                  :name="$t('operator')"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    autocomplete="off"
                    outlined
                    v-model="expense.operator"
                    :error-messages="errors[0]"
                    :label="$t('operator')"
                  />
                </ValidationProvider>
              </v-col>

              <!-- Description Field -->
              <v-col cols="12" lg="3" md="3" sm="6">
                <ValidationProvider
                  vid="description"
                  :name="$tc('description', 1)"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    autocomplete="off"
                    outlined
                    :disabled="isPaymentFlow"
                    v-model="expense.description"
                    :error-messages="errors[0]"
                    :label="$tc('description', 1)"
                    color="secondary"
                  />
                </ValidationProvider>
              </v-col>

              <!-- Reference Field -->
              <v-col
                v-if="!recurrent || isPaymentFlow"
                cols="12"
                lg="3"
                md="3"
                sm="6"
              >
                <ValidationProvider
                  vid="reference"
                  :name="
                    isPaymentFlow ? 'Número de cuenta' : $tc('reference', 1)
                  "
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    autocomplete="off"
                    outlined
                    v-model="expense.reference"
                    :error-messages="errors[0]"
                    :label="
                      isPaymentFlow ? 'Número de cuenta' : $tc('reference', 1)
                    "
                    color="secondary"
                  />
                </ValidationProvider>
              </v-col>

              <!-- Recurrent Type Selection -->
              <v-col
                v-if="recurrent && !isPaymentFlow"
                cols="12"
                lg="3"
                md="3"
                sm="6"
              >
                <ValidationProvider
                  vid="type_recurrent"
                  :name="$tc('type_recurrent', 1)"
                  :rules="{ required: recurrent }"
                  v-slot="{ errors }"
                >
                  <v-select
                    autocomplete="off"
                    outlined
                    v-model="expense.type_recurrent"
                    :error-messages="errors[0]"
                    item-text="state"
                    item-value="abbr"
                    :items="[
                      { state: $t('yearly'), abbr: 'year' },
                      { state: $t('monthly'), abbr: 'month' },
                      { state: $t('weekly'), abbr: 'week' }
                    ]"
                    :label="$t('type_recurrent')"
                  />
                </ValidationProvider>
              </v-col>

              <!-- Payment Type Selection -->
              <v-col
                v-if="!recurrent || isPaymentFlow"
                cols="12"
                lg="3"
                md="3"
                sm="6"
              >
                <ValidationProvider
                  vid="pay"
                  :name="$t('paymenttype')"
                  v-slot="{ errors }"
                >
                  <v-select
                    autocomplete="off"
                    outlined
                    v-model="expense.pay"
                    :error-messages="errors[0]"
                    item-text="state"
                    item-value="abbr"
                    :items="[
                      { state: $t('cc'), abbr: 'cc' },
                      { state: $t('cash'), abbr: 'cash' },
                      { state: $t('check'), abbr: 'check' }
                    ]"
                    :label="$t('paymenttype')"
                  />
                </ValidationProvider>
              </v-col>

              <!-- Payment Field -->
              <v-col
                cols="12"
                lg="3"
                md="3"
                sm="6"
                v-if="!recurrent || isPaymentFlow"
              >
                <ValidationProvider
                  vid="payment"
                  :name="$tc('payment_amount', 1)"
                  rules="required|min_value:0.01"
                  v-slot="{ errors }"
                >
                  <i-money
                    outlined
                    v-model="expense.payment"
                    :error-messages="errors[0]"
                    :label="$tc('payment_amount', 1)"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>
          </v-card-text>
        </form>
      </ValidationObserver>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import dialCategory from '../expenseCategory/modal.vue'
import dialAccount from '../expenseAccount/modal.vue'

export default {
  components: {
    dialCategory,
    dialAccount
  },
  props: {
    recurrent: {
      type: Boolean,
      default: false
    },
    title: String
  },
  data() {
    return {
      today: new Date().toISOString().substr(0, 10),
      expense: {
        category_id: 0,
        account_id: 0,
        start_date: '',
        recurrent_start_date: '',
        operator: '',
        description: '',
        cost: 0.0,
        payment: 0.0,
        pay: 'cash',
        reference: '',
        recurrent: this.recurrent,
        type_recurrent: '',
        push: this.recurrent
      },
      load: false,
      loading: false,
      categories: [],
      accounts: [],
      dialogCategory: false,
      dialogAccount: false,
      toEditCategory: null,
      toEditAccount: null
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/getCompanyData',
      getPermissions: 'session/getPermissions',
      isAdmin: 'session/isAdmin',
      me: 'session/me'
    }),
    isPaymentFlow() {
      return !!this.$route.query.base_id
    }
  },
  methods: {
    /**
     * submit
     * Método para enviar el formulario de gasto, ya sea creando un nuevo gasto
     * o editando uno existente. Muestra un mensaje de éxito al finalizar la
     * operación.
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async submit() {
      this.load = true
      try {
        if (this.recurrent) {
          this.expense.push = true
          this.expense.cost = 0.0
          this.expense.payment = 0.0
        } else {
          this.expense.cost = Number(this.expense.payment) || 0.0
        }
        if (this.expense.pk) {
          await this.$api.expense.edit({
            pk: this.expense.pk,
            form: this.expense
          })
        } else {
          this.expense.recurrent_start_date = this.expense.start_date
          await this.$api.expense.create({ form: this.expense })
        }
        this.$toast.success(
          `${this.$tc('expense', 1)} ${this.$tc(
            this.expense.pk ? 'edited' : 'created',
            2
          )}`
        )
        if (!this.expense.pk && this.recurrent) {
          new Audio('/sounds/sound.mp3').play()
        }

        if (this.isPaymentFlow && this.$route.query.event_id) {
          try {
            await this.$api.notifications.remove({
              pk: this.$route.query.event_id
            })
          } catch (e) {
            console.error('Failed to remove notification:', e)
          }
        }

        this.$root.$emit('payment-processed')

        this.$router.push({
          name: this.recurrent ? 'expensesRecurring' : 'expensesRegular'
        })
      } catch (err) {
        console.error('submit expense error', err)
        this.$toast.error(this.$t('error_saving') || 'Error saving')
      } finally {
        this.load = false
        await Promise.all([this.getAccounts(), this.getCategories()])
      }
    },

    /**
     * getExpenses
     * Método para obtener los detalles de un gasto. Inicializa un nuevo gasto
     * si se está creando o carga los detalles de un gasto existente si se está
     * editando.
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getExpenses() {
      this.loading = true
      try {
        const validation = this.codification({
          mode: 'decode',
          code: this.$route.params.pk
        })
        if (validation === 'create') {
          if (this.$store.getters['utils/getIsloading']) {
            this.$store.dispatch('utils/actionLoading')
            this.$route.meta.title = this.$t('create', { model: '' })
          }
          this.expense = { ...this.expense, recurrent: this.recurrent }
          if (this.isPaymentFlow) {
            const res = await this.$api.expense.show({
              pk: this.$route.query.base_id
            })
            const baseExpense = res.data
            this.expense.account_id = baseExpense.account_id
            this.expense.category_id = baseExpense.category_id
            this.expense.description = baseExpense.description
            this.expense.cost = baseExpense.cost
            this.expense.start_date = this.$route.query.date || this.today
            this.expense.recurrent = false
            this.expense.base_id = this.$route.query.base_id
          }
        } else {
          const res = await this.$api.expense.show({ pk: validation })
          this.expense = res.data
          this.$route.meta.title = this.$t('edit', { model: '' })
        }
      } catch (err) {
        console.error('getExpenses error', err)
        this.$router.push({ name: 'expensesRegular' })
      } finally {
        this.loading = false
      }
    },

    /**
     * getAccounts
     * Método para obtener la lista de cuentas desde la API y actualizar el
     * estado de cuentas en el componente.
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getAccounts() {
      try {
        const account = await this.$api.expense.account.list({})
        this.accounts = account.data.results || []
      } catch (err) {
        console.error('getAccounts error', err)
      }
    },

    /**
     * addAccount
     * Método para agregar nueva cuenta a la lista, si se proporciona un evento.
     *
     * @param {Object} evt - Evento que puede contener una nueva cuenta
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    addAccount(evt) {
      this.accounts.push(evt)
      this.expense.account_id = evt.pk
    },

    /**
     * getCategories
     * Método para obtener la lista de categorías desde la API y actualizar el
     * estado de categorías en el componente
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getCategories() {
      try {
        const category = await this.$api.expense.category.list({})
        this.categories = category.data.results || []
      } catch (err) {
        console.error('getCategories error', err)
      }
    },

    /**
     * addCategory
     * Método para agregar nueva categoría a la lista, si se proporciona un
     * evento.
     *
     * @param {Object} evt - Evento que puede contener una nueva categoría
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    addCategory(evt) {
      this.categories.push(evt)
      this.expense.category_id = evt.pk
    },

    /**
     * openCategoryModal
     * Método para abrir el modal de categoría.
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    openCategoryModal() {
      this.dialogCategory = true
    },
    /**
     * openAccountModal
     * Método para abrir el modal de cuenta.
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    openAccountModal() {
      this.dialogAccount = true
    }
  },
  async mounted() {
    await Promise.all([
      this.getCategories(),
      this.getAccounts(),
      this.getExpenses()
    ])
  }
}
</script>
